import { useState } from 'react';
import type { ReplayReactionEvent } from '../../shared/replayReactionEvent';
import { ApiRequestError } from '../api/apiRequestError';
import { getReviewerReplayStream } from '../api/getReviewerReplayStream';

// Shown instead of the legend when this browser has already finished this test
// (Cowork016 §1).
//
// The legend screen promises "finish the video and you'll see how everyone else
// reacted". For reviewer #1 that promise is empty — they finish and watch their
// own four taps float past. The reward was distributed backwards: whoever helped
// first got the least. This is the way back in.
//
// Safe by the same reasoning that allowed the summary replay at all: their own
// reactions are already recorded and immutable, so nothing they see now can
// contaminate anything.
//
// It also means a returning reviewer never reaches the Start button, which is
// what stops them being recorded a second time (§2).
export function ViewerReturningScreen(input: {
  active: boolean;
  title: string | null;
  reviewerId: string;
  onOpenReplay: (events: ReplayReactionEvent[]) => void;
  onSessionGone: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      input.onOpenReplay(await getReviewerReplayStream(input.reviewerId));
    } catch (caught) {
      // 404 means the reviewer row is gone — the test was deleted, or an admin
      // removed them on request. Fall back to being a first-time visitor rather
      // than stranding them on a dead screen.
      if (caught instanceof ApiRequestError && caught.status === 404) {
        input.onSessionGone();
        return;
      }
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load the replay.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={input.active ? 'screen screenOn screenScroll' : 'screen'}>
      <div className="pageWrap">
        <h1>
          what<span>landed</span>
        </h1>
        <p className="pageSub">
          {input.title ? `“${input.title}”` : 'This one'} — you've already reacted to this one.
        </p>

        <div className="callout">
          <b>Your reactions are already in.</b> You don't need to watch again. Have a look at
          how everyone else felt — there may be more of them now than when you finished.
        </div>

        <button className="buttonPrimary" type="button" onClick={() => void open()} disabled={loading}>
          {loading ? 'Loading…' : 'See what everyone else felt'}
        </button>

        {error ? <div className="calloutError">{error}</div> : null}
      </div>
    </section>
  );
}
